<template>
    <button
        v-if="isBenefitFullyRedeemedThisYear"
        class="btn-icon btn-white opacity-50 cursor-not-allowed !bg-white dark:!bg-slate-800"
        disabled
        @click.stop
    >
        <Icon icon="plus" svgClass="w-4 h-4 text-slate-600 dark:text-slate-300" />
    </button>
    <ButtonPopover
        v-else
        buttonClass="btn-icon btn-white"
        popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-lg min-w-80 z-40 outline outline-1 outline-offset-[-1px] outline-slate-200 dark:outline-slate-700"
        @click.stop
    >
        <template #button-content>
            <Icon icon="plus" svgClass="w-4 h-4 text-slate-600 dark:text-slate-300" />
        </template>
        <template #default="{ closePopover }">
            <div class="p-4 w-[360px]" @click.stop>
                <BenefitsUsageForm
                    v-model="usageFormData"
                    :hideLabels="true"
                    :errors="formErrors"
                    :isCountBased="isCountBased"
                    :countLabel="countLabel"
                    :maxAmount="maxFillableAmount"
                    :maxCount="remainingCount"
                />

                <div
                    v-if="!isCountBased"
                    class="flex justify-between items-center mt-4"
                >
                    <div
                        v-if="
                            isByCountValueBenefit
                                ? parseFloat(remainingCount) > 0
                                : parseFloat(remainingAmount) > 0
                        "
                        class="text-slate-500 dark:text-slate-400 text-sm"
                    >
                        <template v-if="isByCountValueBenefit">
                            {{ remainingCount }} remaining
                        </template>
                        <template v-else>
                            ${{ remainingAmount }} remaining
                        </template>
                    </div>
                    <Errors
                        v-else
                        :errors="[
                            {
                                message: isByCountValueBenefit
                                    ? 'No remaining uses!'
                                    : 'No remaining value!',
                            },
                        ]"
                    />

                    <button
                        class="bg-slate-800 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-slate-700 transition-colors flex items-center space-x-2"
                        @click.stop="handleUsageSubmit(closePopover)"
                    >
                        <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                        <span>Redeemed</span>
                    </button>
                </div>
                <div v-else class="flex justify-between items-center mt-4">
                    <div
                        v-if="parseFloat(remainingCount) > 0"
                        class="text-slate-500 dark:text-slate-400 text-sm"
                    >
                        {{ remainingCount }} remaining
                    </div>
                    <Errors
                        v-else
                        :errors="[{ message: 'No remaining uses!' }]"
                    />

                    <button
                        class="bg-slate-800 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-slate-700 transition-colors flex items-center space-x-2"
                        @click.stop="handleUsageSubmit(closePopover)"
                    >
                        <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                        <span>Redeemed</span>
                    </button>
                </div>
            </div>
        </template>
    </ButtonPopover>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import dayjs from 'dayjs';
import { useCardBenefitsStore } from '@/store/card-benefits';
import ButtonPopover from '@/components/ui/button-popover.vue';
import BenefitsUsageForm from '@/components/card-benefits/benefits-usage-form.vue';
import Errors from '@/components/errors.vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    benefitItem: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['add-usage']);

const store = useCardBenefitsStore();
const { calculateDisplayAmount } = store;

const formErrors = ref({});
const usageFormData = ref({
    amount: 0,
    date: dayjs().format('YYYY-MM-DD'),
    count: 0,
});

const liveBenefit = computed(() => {
    return (
        store.benefits.find(
            (b) =>
                b.benefit.id === props.benefitItem.benefit.id &&
                b.connected_account_id ===
                    props.benefitItem.connected_account_id
        ) || props.benefitItem
    );
});

const isCountBased = computed(() =>
    store.checkIsCountBased(props.benefitItem?.benefit)
);

const countLabel = computed(
    () => props.benefitItem?.benefit?.value_details?.count_label || ''
);

const displayAmount = computed(() => {
    const benefit = liveBenefit.value;
    if (!benefit) return 0;
    if (benefit.benefit.period_type?.toLowerCase() === 'monthly') {
        return store.calculateMonthlyValue(benefit);
    }
    return calculateDisplayAmount(benefit);
});

const displayAmountForSelectedDate = computed(() => {
    const benefit = liveBenefit.value;
    if (!benefit) return 0;

    const valueDetails = benefit.benefit.value_details || {};
    const count = parseFloat(valueDetails.count) || 1;
    const baseAmount = parseFloat(benefit.benefit.amount) || 0;
    const isCountBasedValue = benefit.benefit.values_summary?.is_count_based;

    if (benefit.benefit.period_type?.toLowerCase() === 'monthly') {
        const overrideMonth = valueDetails.override_month?.month;
        const overrideValue = valueDetails.override_month?.value;

        if (
            overrideMonth &&
            overrideValue != null &&
            usageFormData.value.date
        ) {
            const selectedMonth = String(
                dayjs(usageFormData.value.date).month() + 1
            ).padStart(2, '0');

            if (overrideMonth === selectedMonth) {
                return (parseFloat(overrideValue) || 0) * count;
            }
        }

        return isCountBasedValue ? baseAmount * count : baseAmount;
    }

    return calculateDisplayAmount(benefit);
});

const currentPeriodUsage = computed(() => {
    const benefit = liveBenefit.value;
    if (!benefit) return 0;
    const recalculated = store.recalculateCurrentPeriodUsage(benefit);
    return recalculated.current_period_usage || 0;
});

const usageForSelectedPeriod = computed(() => {
    if (!usageFormData.value.date || !liveBenefit.value) return 0;

    const periodType = liveBenefit.value.benefit?.period_type;
    const selectedPeriodKey = store.getPeriodKey(
        usageFormData.value.date,
        periodType
    );
    const currentPeriodKey = store.getPeriodKey(new Date(), periodType);

    if (selectedPeriodKey === currentPeriodKey) {
        return liveBenefit.value.current_period_usage || 0;
    }

    const usageRecords = liveBenefit.value.usage_records || [];
    return usageRecords
        .filter(
            (record) =>
                store.getPeriodKey(record.used_at, periodType) ===
                selectedPeriodKey
        )
        .reduce(
            (sum, record) => sum + (parseFloat(record.total_amount_used) || 0),
            0
        );
});

const remainingAmount = computed(() => {
    const benefitAmount = displayAmountForSelectedDate.value;
    const usedAmount = usageForSelectedPeriod.value;
    return Math.max(0, benefitAmount - usedAmount).toFixed(2);
});

// By-count benefits that still carry a dollar value (e.g. 2 × $250): the user
// enters a $ amount (isCountBased is false), but the meaningful "remaining"
// unit is the number of uses, capped at value_details.count by the backend
// ValidateBenefitUsageCount rule.
const isByCountValueBenefit = computed(() => {
    if (isCountBased.value) {
        return false;
    }
    const count =
        parseFloat(liveBenefit.value?.benefit?.value_details?.count) || 1;
    return count > 1;
});

// Uses recorded in the selected date's period. For by-count benefits with a
// dollar value each record is one use — count records (usage_count may be 0 on
// records logged before the benefit became by-count). Pure count-based
// benefits sum usage_count, using the recalculated count for the current
// period (handles anniversary reset basis + optimistic updates).
const countUsedForSelectedPeriod = computed(() => {
    if (!usageFormData.value.date || !liveBenefit.value) return 0;

    const periodType = liveBenefit.value.benefit?.period_type;
    const selectedPeriodKey = store.getPeriodKey(
        usageFormData.value.date,
        periodType
    );
    const currentPeriodKey = store.getPeriodKey(new Date(), periodType);

    const recordsInPeriod = (liveBenefit.value.usage_records || []).filter(
        (record) =>
            store.getPeriodKey(record.used_at, periodType) === selectedPeriodKey
    );

    if (isByCountValueBenefit.value) {
        return recordsInPeriod.length;
    }

    if (selectedPeriodKey === currentPeriodKey) {
        return (
            store.recalculateCurrentPeriodUsage(liveBenefit.value)
                .count_used_current_period || 0
        );
    }
    return recordsInPeriod.reduce(
        (sum, record) => sum + (parseFloat(record.usage_count) || 0),
        0
    );
});

const remainingCount = computed(() => {
    const totalCount =
        parseFloat(liveBenefit.value?.benefit?.value_details?.count) || 0;
    return Math.max(0, totalCount - countUsedForSelectedPeriod.value);
});

// Per-transaction cap for by-count benefits (value_details.count > 1): a
// single entry can't exceed the version's per-use amount even when the
// period still has room (e.g. a 2 × $250 credit caps each entry at $250
// though $500 remains). Mirrors the backend
// ValidateBenefitUsagePerTransactionAmount rule. displayAmountForSelectedDate
// is the period total here, so dividing by count yields the per-use amount
// with the same monthly override-month handling. Infinity (no cap) for
// single-allotment benefits.
const perTransactionMaxAmount = computed(() => {
    const count =
        parseFloat(props.benefitItem?.benefit?.value_details?.count) || 1;
    if (count <= 1) return Infinity;
    return (parseFloat(displayAmountForSelectedDate.value) || 0) / count;
});

const isBenefitFullyRedeemedThisYear = computed(() =>
    store.isBenefitFullyRedeemedThisYear(props.benefitItem)
);

// Largest amount the "Max" pill can fill: the period's remaining value, capped
// by the per-transaction limit for by-count benefits (mirrors the validate()
// effectiveMaxAmount used to reject over-entries).
const maxFillableAmount = computed(() =>
    Math.min(parseFloat(remainingAmount.value), perTransactionMaxAmount.value)
);

const validate = () => {
    formErrors.value = {};
    if (!isCountBased.value) {
        if (!usageFormData.value.amount) {
            formErrors.value.usageAmount = 'Enter amount';
        }
        if (!usageFormData.value.date) {
            formErrors.value.usageDate = 'Enter date';
        }
        const effectiveMaxAmount = Math.min(
            parseFloat(remainingAmount.value),
            perTransactionMaxAmount.value
        );
        if (usageFormData.value.amount > effectiveMaxAmount) {
            formErrors.value.usageAmount = `Max amount is ${effectiveMaxAmount.toFixed(2)}`;
            if (props.benefitItem?.benefit?.value_details?.count > 1) {
                formErrors.value.usageAmount += ` per use`;
            }
        }
        // By-count benefits are also capped on the number of uses per period
        // (mirrors backend ValidateBenefitUsageCount). Each amount entry is one
        // use, so block once the period's uses are exhausted.
        if (
            isByCountValueBenefit.value &&
            parseFloat(remainingCount.value) < 1
        ) {
            formErrors.value.usageAmount = 'No remaining uses this period';
        }
    } else {
        if (usageFormData.value.count > parseFloat(remainingCount.value)) {
            formErrors.value.usageCount = `Max uses is ${remainingCount.value}`;
        }
        if (!usageFormData.value.count) {
            formErrors.value.usageCount = 'Enter count';
        }
    }
    return Object.keys(formErrors.value).length === 0;
};

const handleUsageSubmit = async (closePopover) => {
    if (!validate()) return;

    const success = await store.handleUsageSubmit(
        usageFormData.value,
        props.benefitItem,
        closePopover
    );

    if (success) {
        usageFormData.value = {
            amount: '',
            date: dayjs().format('YYYY-MM-DD'),
            count: 0,
        };
        emit('add-usage', props.benefitItem);
        if (typeof closePopover === 'function') {
            closePopover();
        }
    }
};

watch(
    () => usageFormData.value,
    () => {
        formErrors.value = {};
    },
    { deep: true }
);
</script>
